/**
 * changeJourneySteps — the pure step-derivation logic behind ChangeJourney
 * (Saved → Checks passed → Publishing/Published → Live in production). Kept
 * out of the *.tsx component file (and out of a mixed component+helper
 * export) so it isn't co-located with a component export — avoids the
 * react-refresh/only-export-components lint warning a mixed export from the
 * same file would trigger (see src/utils/investigationStatus.ts for the same
 * pattern), and lets these functions be unit-tested directly.
 *
 * ADR-110 Layer B: for an already-merged PR, "Live in production" must
 * reflect the ACTUAL Deploy Fleet workflow-run result (`deploy`), not just
 * that the PR merged to main — merged ≠ deployed (a merge can land on main
 * while the deploy-fleet.yml run against it then fails, e.g. the ADR-105
 * deploy-lag guard reds a stale-image deploy). See deployLiveState.
 */
import type { DeployInfo, ImprovementPRChecks } from '../api/client'

export interface ApproveOutcome {
  merged_to_develop: boolean
  promoted: boolean
  queued: boolean
  /** Set when the merge succeeded but the develop→main promote failed. */
  promoteFailed?: boolean
}

export type StepState = 'done' | 'active' | 'failed' | 'pending'

export interface Step {
  label: string
  state: StepState
}

export function checksLabel(checks: ImprovementPRChecks): string {
  switch (checks) {
    case 'passing': return 'Checks passed'
    case 'failing': return 'Checks failed'
    case 'unreadable': return 'Checks unreadable'
    case 'stalled': return 'Checks stalled'
    case 'pending': return 'Checks running'
    case 'none': return 'No checks reported'
  }
}

/**
 * deployLiveState derives the "Live in production" step's state for an
 * already-merged PR from the real Deploy Fleet run (ADR-110 Layer B) —
 * NEVER unconditionally 'done' just because the PR merged.
 *
 * - No deploy info yet, no run found ('none'), or a run exists but doesn't
 *   yet cover this change (`includes_change === false`): 'active' — the
 *   change is still on its way to production, not live and not failed.
 * - A run that covers this change but hasn't completed (queued/in_progress,
 *   conclusion empty): 'active'.
 * - Completed with conclusion 'success': 'done'.
 * - Completed with a failing conclusion (failure/cancelled/timed_out/
 *   action_required): 'failed' — this is the bug this feature fixes: a
 *   merged PR whose deploy then failed must never read as "live".
 * - Any other completed conclusion (skipped/neutral/stale): 'active', since
 *   none of those affirmatively confirm the change is running in production.
 */
export function deployLiveState(deploy?: DeployInfo | null): StepState {
  if (!deploy || deploy.status === 'none' || !deploy.includes_change) return 'active'
  if (deploy.status !== 'completed') return 'active'
  switch (deploy.conclusion) {
    case 'success':
      return 'done'
    case 'failure':
    case 'cancelled':
    case 'timed_out':
    case 'action_required':
      return 'failed'
    default:
      return 'active'
  }
}

export function buildSteps(checks: ImprovementPRChecks, approve?: ApproveOutcome, merged?: boolean, deploy?: DeployInfo | null): Step[] {
  // 'none' is NOT 'passed'. The old expression treated every value that was not
  // `failing` or `pending` as done, so a PR with no checks reported rendered a green
  // "✓ Checks passed" pill directly above the page's own warning that the change is
  // unverified — the same field, read two contradictory ways on one screen (spartan
  // #880, 2026-08-20). A reviewer cannot tell which half to believe, so the pill has to
  // agree with the warning: nothing has reported, so nothing has passed.
  // 'unreadable' and 'stalled' are 'failed', not 'active'. Neither can be cleared
  // by waiting — an unreadable check is one we cannot see, and a stalled one
  // reached no verdict — so rendering either as in-progress is the same false
  // reassurance 'none' gave: the journey looks like it is advancing while nothing
  // can advance it. Both block branch protection, so both belong on the red side.
  const checksState: StepState =
    checks === 'failing' || checks === 'unreadable' || checks === 'stalled'
      ? 'failed'
      : checks === 'passing'
        ? 'done'
        : 'active'

  let publishingState: StepState = 'pending'
  let liveState: StepState = 'pending'

  // A PR that is already merged (whether via this button or out-of-band) has
  // reached main — Saved/Checks passed/Published are done. "Live in
  // production" is NOT assumed done, though: it's derived from the actual
  // Deploy Fleet run (see deployLiveState) so a merge whose deploy then
  // failed shows failed, not a false green.
  if (merged) {
    return [
      { label: 'Saved', state: 'done' },
      { label: 'Checks passed', state: 'done' },
      { label: 'Published', state: 'done' },
      { label: 'Live in production', state: deployLiveState(deploy) },
    ]
  }

  if (approve) {
    publishingState = approve.merged_to_develop ? 'done' : 'pending'
    if (approve.promoteFailed) {
      liveState = 'failed'
    } else if (approve.promoted || approve.merged_to_develop) {
      // A promote that merged has only started the deploy. deployLiveState says
      // when it is live, once the page's re-read sees the PR as merged.
      liveState = 'active'
    }
  }

  return [
    { label: 'Saved', state: 'done' },
    { label: checksLabel(checks), state: checksState },
    { label: 'Publishing', state: publishingState },
    { label: 'Live in production', state: liveState },
  ]
}
